/**
 * FirebaseAnalyticsService - Wrapper around existing AnalyticsService
 *
 * This wraps the existing AnalyticsService to implement IAnalyticsService interface.
 * Used in hosted mode when Firebase credentials are provided.
 */

import type { Analytics } from 'firebase/analytics';
import type { Firestore } from 'firebase/firestore';
import { AnalyticsService } from '../../analytics/AnalyticsService';
import type { IAnalyticsService } from '../interfaces';

/**
 * Firebase-backed analytics service for hosted mode
 * Wraps the existing AnalyticsService implementation
 */
export class FirebaseAnalyticsService implements IAnalyticsService {
  private analyticsService: AnalyticsService;

  constructor(analytics: Analytics | null, firestore: Firestore | null) {
    this.analyticsService = new AnalyticsService(analytics, firestore);
  }

  // Delegate all methods to the wrapped AnalyticsService

  setContext(sequenceId: string | null, userId: string | null): void {
    this.analyticsService.setContext(sequenceId, userId);
  }

  setEnabled(enabled: boolean): void {
    this.analyticsService.setEnabled(enabled);
  }

  trackSessionStarted(): void {
    this.analyticsService.trackSessionStarted();
  }

  trackSessionEnded(): void {
    this.analyticsService.trackSessionEnded();
  }

  trackSequenceLoaded(
    sequenceName: string,
    isPublic: boolean,
    hasPassword: boolean,
    organizationId: string | null
  ): void {
    this.analyticsService.trackSequenceLoaded(
      sequenceName,
      isPublic,
      hasPassword,
      organizationId
    );
  }

  trackSequenceViewStarted(): void {
    this.analyticsService.trackSequenceViewStarted();
  }

  trackSequenceViewCompleted(): void {
    this.analyticsService.trackSequenceViewCompleted();
  }

  trackStageNavigation(
    fromStage: number,
    toStage: number,
    method: 'next' | 'previous' | 'jump' | 'selector',
    totalStages: number
  ): void {
    this.analyticsService.trackStageNavigation(fromStage, toStage, method, totalStages);
  }

  trackStageViewStarted(
    stageIndex: number,
    stageName: string | undefined,
    hasVideo: boolean,
    hasModels: boolean,
    hasPlanes: boolean,
    hasAudio: boolean
  ): void {
    this.analyticsService.trackStageViewStarted(
      stageIndex,
      stageName,
      hasVideo,
      hasModels,
      hasPlanes,
      hasAudio
    );
  }

  trackStageViewEnded(stageIndex: number, stageName: string | undefined): void {
    this.analyticsService.trackStageViewEnded(stageIndex, stageName);
  }

  trackStageSelectorOpened(mode: '2D' | 'VR', stageIndex: number): void {
    this.analyticsService.trackStageSelectorOpened(mode, stageIndex);
  }

  trackStageSelectorClosed(mode: '2D' | 'VR', stageIndex: number): void {
    this.analyticsService.trackStageSelectorClosed(mode, stageIndex);
  }

  trackVRSessionStarted(): void {
    this.analyticsService.trackVRSessionStarted();
  }

  trackVRSessionEnded(): void {
    this.analyticsService.trackVRSessionEnded();
  }

  trackVRStateChanged(state: 'entering' | 'in' | 'exiting' | 'not_in'): void {
    this.analyticsService.trackVRStateChanged(state);
  }

  trackVRControllerConnected(
    handedness: 'left' | 'right' | 'unknown',
    controllerType?: string
  ): void {
    this.analyticsService.trackVRControllerConnected(handedness, controllerType);
  }

  trackVideoPlay(stageIndex: number, videoUrl?: string): void {
    this.analyticsService.trackVideoPlay(stageIndex, videoUrl);
  }

  trackVideoPause(stageIndex: number, currentTime?: number): void {
    this.analyticsService.trackVideoPause(stageIndex, currentTime);
  }

  trackVideoComplete(stageIndex: number, duration?: number): void {
    this.analyticsService.trackVideoComplete(stageIndex, duration);
  }

  trackVideoError(stageIndex: number, errorMessage: string): void {
    this.analyticsService.trackVideoError(stageIndex, errorMessage);
  }

  trackFOVZoom(newFOV: number, previousFOV: number, method: 'wheel' | 'pinch'): void {
    this.analyticsService.trackFOVZoom(newFOV, previousFOV, method);
  }

  trackControllerButton(
    button: 'trigger-left' | 'trigger-right' | 'a' | 'b' | 'x' | 'y',
    action: string,
    stageIndex?: number
  ): void {
    this.analyticsService.trackControllerButton(button, action, stageIndex);
  }

  trackPerformance(fps: number, stageIndex?: number): void {
    this.analyticsService.trackPerformance(fps, stageIndex);
  }

  trackStageLoadTime(stageIndex: number, loadTime: number): void {
    this.analyticsService.trackStageLoadTime(stageIndex, loadTime);
  }

  trackMediaLoadTime(
    resourceType: 'model' | 'video' | 'image',
    resourceUrl: string,
    loadTime: number,
    stageIndex?: number
  ): void {
    this.analyticsService.trackMediaLoadTime(resourceType, resourceUrl, loadTime, stageIndex);
  }

  trackError(
    errorType: string,
    errorMessage: string,
    stack?: string,
    stageIndex?: number
  ): void {
    this.analyticsService.trackError(errorType, errorMessage, stack, stageIndex);
  }

  getCurrentStageIndex(): number {
    return this.analyticsService.getCurrentStageIndex();
  }

  getIsInVR(): boolean {
    return this.analyticsService.getIsInVR();
  }

  getSessionId(): string {
    return this.analyticsService.getSessionId();
  }

  dispose(): void {
    this.analyticsService.dispose();
  }
}
